/**
 * PSE-2 — Sentiment polarity + BTM-tech classification helpers.
 *
 * Shared utilities used by the three PSE-2 source fetchers
 * (`gdeltSentiment`, `localMinutesSentiment`, `federalDocketsSentiment`).
 * Kept pure + side-effect free so the fetcher unit tests can pin the
 * classification corpus without HTTP mocking.
 *
 * Three responsibilities:
 *  1. `classifyBtmTech(text)` — pattern-match an article body / minutes
 *     excerpt / docket filing for the dominant BTM-tech bucket. Falls
 *     back to `'general-datacenter'` when no specific tech wins.
 *  2. `polarityScore(text)` — simple keyword-polarity scorer in [−1, +1]
 *     suitable for council-minutes excerpts + short news headlines. The
 *     three federal-docket fetchers can optionally swap in a smarter
 *     scorer (Gemini-backed); the keyword scorer is the universal
 *     fallback when the cost-cap is hit or the text is too short to
 *     justify an LLM call.
 *  3. `lengthBasedConfidence(text)` — confidence ∈ [0, 1] driven by text
 *     length. Longer text → higher confidence the classification + tone
 *     extraction is trustworthy. Capped at 1.0 at 2000 chars (the
 *     longest sample that meaningfully improves the signal-to-noise
 *     for short-context classification).
 *
 * Score conventions:
 *  - `+1` = strongly supportive ("approve", "recommend", "support").
 *  - `0`  = neutral / no signal.
 *  - `−1` = strongly opposed ("oppose", "deny", "moratorium").
 *
 * Confidence is intentionally NOT a function of polarity magnitude —
 * a strongly-negative excerpt with three opposition keywords doesn't get
 * a confidence boost over a neutral excerpt of equal length. The
 * orchestrator's confidence-weighted aggregator already gives high-
 * confidence observations more pull; doubling that up here would
 * over-weight strident voices.
 */

import type { PublicSentimentBtmTech } from '../../types/siteEvidence';

/* ── BTM-tech classification ───────────────────────────────────────── */

/**
 * Per-bucket keyword vocabulary. Order matters — earlier entries win when
 * multiple buckets match (e.g. an article that mentions both "SMR" and
 * "solar" lands in `nuclear-smr` because we list nuclear first; the more
 * specific tech is usually the headline tech). Keywords are lowercased
 * before matching; the matcher itself is case-insensitive.
 *
 * Each pattern is a substring; we deliberately avoid `\b` word boundaries
 * because GDELT article bodies + council minutes often run keywords
 * together with adjacent punctuation ("(SMR)", "SMR's", "SMR-based").
 */
const BTM_TECH_VOCAB: Array<{ tech: PublicSentimentBtmTech; keywords: string[] }> = [
  {
    tech: 'nuclear-smr',
    keywords: [
      'small modular reactor',
      'smr',
      'nuclear reactor',
      'nuclear plant',
      'nuclear power',
      'esp ', 'early site permit',
      'cola', 'combined license',
      'nrc',
    ],
  },
  {
    tech: 'natural-gas',
    keywords: [
      'natural gas',
      'gas turbine',
      'gas-fired',
      'methane',
      'lng',
      'gas peaker',
      'combined cycle',
    ],
  },
  {
    tech: 'solar-bess',
    keywords: [
      'solar farm',
      'solar array',
      'utility scale solar',
      'utility-scale solar',
      'photovoltaic',
      'pv farm',
      'battery storage',
      'battery energy storage',
      'bess',
    ],
  },
  {
    tech: 'wind-bess',
    keywords: [
      'wind farm',
      'wind turbine',
      'wind project',
      'turbine setback',
      'offshore wind',
      'onshore wind',
    ],
  },
  {
    tech: 'general-datacenter',
    keywords: [
      'data center',
      'datacenter',
      'hyperscale',
      'colocation',
      'colo facility',
    ],
  },
];

/**
 * Classify a body of text into one of five BTM-tech buckets.
 *
 * Algorithm:
 *  1. Lowercase the input once.
 *  2. Walk buckets in priority order; first bucket with ≥1 keyword hit wins.
 *  3. If no bucket matches → fall back to `'general-datacenter'` (the
 *     catch-all bucket — any piece of text in the PSE pipeline is at
 *     minimum about data-center siting in general).
 *
 * Defensive: empty / non-string input → `'general-datacenter'` (the
 * caller already filtered the entry in, so a fallback bucket is safer
 * than dropping the observation).
 */
export function classifyBtmTech(text: string | null | undefined): PublicSentimentBtmTech {
  if (typeof text !== 'string' || text.length === 0) return 'general-datacenter';
  const lower = text.toLowerCase();
  for (const { tech, keywords } of BTM_TECH_VOCAB) {
    for (const kw of keywords) {
      if (new RegExp(`\\b${kw.trim()}\\b`, 'i').test(lower)) return tech;
    }
  }
  return 'general-datacenter';
}

/* ── Polarity scoring ──────────────────────────────────────────────── */

/**
 * Polarity vocabulary. Positive list: supportive / approval language.
 * Negative list: opposition / denial language. Both lists deliberately
 * SHORT — false positives are more costly than false negatives in the
 * PSE pipeline (a neutral observation that gets pulled into the +1
 * bucket erroneously inflates the meanSentiment more than missing a
 * weak signal would).
 *
 * Each entry is a substring; case-insensitive match. Word boundaries
 * are not used (same reasoning as `BTM_TECH_VOCAB` — minutes excerpts
 * and docket filings are noisy text).
 */
const POSITIVE_KEYWORDS: readonly string[] = [
  'approve', 'approved', 'approval',
  'support', 'supported', 'supports', 'supportive',
  'recommend', 'recommended', 'recommendation',
  'in favor', 'favourable', 'favorable',
  'endorse', 'endorsed', 'endorsement',
  'green light',
  'unanimous',
  'pass',  // "motion passes" / "passed"
  'adopt', 'adopted',
];

const NEGATIVE_KEYWORDS: readonly string[] = [
  'oppose', 'opposed', 'opposition',
  'deny', 'denied', 'denial',
  'reject', 'rejected', 'rejection',
  'moratorium',
  'against', 'object', 'objection',
  'protest',
  'block', 'blocked',
  'ban', 'banned',
  'concerns', 'concerned',
  'fail',  // "motion fails"
  'withdraw', 'withdrawn',
];

/**
 * Score text polarity in [−1, +1].
 *
 * Algorithm:
 *  1. Count distinct positive-keyword hits in the lowercased text.
 *  2. Count distinct negative-keyword hits.
 *  3. Score = (pos − neg) / max(pos + neg, 1). Falls to 0 cleanly when
 *     both counts are zero (no signal — neutral).
 *  4. Clamp to [−1, +1] defensively.
 *
 * "Distinct" means we count each keyword AT MOST ONCE regardless of
 * how many times it appears. This stops a long article with "oppose"
 * repeated 12 times from pinning the score at −1 — the multiple
 * mentions don't add information past the first.
 *
 * Returns 0 for empty / non-string input.
 */
export function polarityScore(text: string | null | undefined): number {
  if (typeof text !== 'string' || text.length === 0) return 0;
  const lower = text.toLowerCase();
  let pos = 0;
  let neg = 0;
  for (const kw of POSITIVE_KEYWORDS) {
    if (lower.includes(kw)) pos++;
  }
  for (const kw of NEGATIVE_KEYWORDS) {
    if (lower.includes(kw)) neg++;
  }
  const total = pos + neg;
  if (total === 0) return 0;
  const score = (pos - neg) / total;
  // Clamp defensively — the formula already lives in [−1, +1] but a
  // future change to weighting could push out of bounds; the
  // downstream aggregator already clamps, but doing it here keeps
  // bucket boundaries clean.
  return Math.max(-1, Math.min(1, score));
}

/* ── Confidence ────────────────────────────────────────────────────── */

/** Length at which confidence saturates to 1.0. */
const CONFIDENCE_SATURATION_CHARS = 2000;

/**
 * Confidence ∈ [0, 1] from text length.
 *
 *  conf = min(1, length / 2000)
 *
 * Rationale: short snippets (< 200 chars) typically carry one keyword
 * hit — the polarity scorer can't disambiguate sarcasm or context.
 * Long-form text (≥ 2000 chars) gives the scorer enough surface for
 * the score to be trustworthy.
 *
 * Caller is free to override (e.g. for known-high-quality sources like
 * a verified docket comment) by passing a fixed confidence to the
 * `PublicSentimentEntry` constructor and skipping this helper.
 *
 * Returns 0 for empty / non-string input.
 */
export function lengthBasedConfidence(text: string | null | undefined): number {
  if (typeof text !== 'string' || text.length === 0) return 0;
  const conf = text.length / CONFIDENCE_SATURATION_CHARS;
  return Math.max(0, Math.min(1, conf));
}

/* ── Snippet truncation ────────────────────────────────────────────── */

/** Max characters for `supportingSnippet`. Mirrors the SED type's docstring. */
const SNIPPET_MAX_CHARS = 280;

/**
 * Truncate a snippet to the `PublicSentimentEntry.supportingSnippet`
 * length cap, preserving word boundaries when possible. Returns the
 * original text when it's already short enough. Collapses internal
 * whitespace (newlines, tabs, repeated spaces) to single spaces — the
 * snippet renders inline in the UI.
 */
export function truncateSnippet(text: string | null | undefined): string {
  if (typeof text !== 'string') return '';
  const collapsed = text.replace(/\s+/g, ' ').trim();
  if (collapsed.length <= SNIPPET_MAX_CHARS) return collapsed;
  // Truncate at the last word boundary inside the limit so we don't
  // cut a word in half. Falls back to a hard truncate if no boundary
  // exists in the trailing 40 chars.
  const sliced = collapsed.slice(0, SNIPPET_MAX_CHARS);
  const lastSpace = sliced.lastIndexOf(' ');
  if (lastSpace > SNIPPET_MAX_CHARS - 40) {
    return sliced.slice(0, lastSpace) + '…';
  }
  return sliced + '…';
}
